/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const required = value => {
  if (value instanceof File) return fileIsRequired(value);
  return value ? value.trim() !== '' : false;
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const ssn = value => matchesPattern(value, /^\d{3}-?\d{2}-?\d{4}$/);

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const zipCode = value => matchesPattern(value, /^\d{5}(-\d{4})?$/);
zipCode.errorMessage = 'Must be 5 or 9 numbers.';

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const email = value => matchesPattern(value?.trim(), /^(?<localname>[A-Za-z0-9](?:\.?[\w!#%&'`/=~\-$*+?^{}|])*)@(?<domainname>\[(?:\d{1,3}\.){3}\d{1,3}\]|(?:[A-Za-z0-9][-\w]*[A-Za-z0-9]*\.)+[A-Za-z0-9]{2,17})$/);

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {string} value - The value to be validated.
 * @param {number} min - The minimum length allowed.
 * @param {number} max - The maximum length allowed.
 */
export const isLengthInRange = (value, min, max) => value !== undefined && value.length >= min && value.length <= max;

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {string} value - The percentage value to be validated.
 * @param {number} min - The minimum value allowed (default: 0).
 * @param {number} max - The maximum value allowed (default: 100).
 */
export const isPercentageInRange = function (value) {
  let min = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  let max = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 100;
  const numberValue = parseFloat(value);
  return isNumberInRange(numberValue, min, max);
};
/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {number} value
 * @param {number} max - The maximum value allowed (default: 100).
 */
export const isPercentageLessThanOrEqualTo = function (value) {
  let max = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 100;
  const numberValue = parseFloat(value);
  return isNumberLessThanOrEqualTo(numberValue, max);
};
/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {number} value
 * @param {number} min - The minimum value allowed (default: 0).
 */
export const isPercentageGreaterThanOrEqualTo = function (value) {
  let min = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  const numberValue = parseFloat(value);
  return isNumberGreaterThanOrEqualTo(numberValue, min);
};

/**
 * Parses a currency value string into a number. Supports 'USD' and 'CAD' currencies.
 * @param {string} value - The currency value
 * @param {string} currency - The currency code (ISO 4217) to use. (default: 'USD')
 * @throws {Error} If the currency code is not supported or the value is not a string.
 * @returns {number}
 */
const parseNumberFromCurrencyValue = function (value) {
  let currency = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'USD';
  if (typeof value !== 'string') {
    throw new Error('Value must be a string.');
  }
  switch (currency) {
    case 'CAD':
    case 'USD':
      return parseFloat(value.replace(/[$,]/g, ''));
    default:
      throw new Error(`Currency code "${currency}" is not supported.`);
  }
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {string} value - The currency value to be validated.
 * @param {number} min - The minimum value allowed.
 * @param {number} max - The maximum value allowed.
 * @param {string} currency - The currency code (ISO 4217) to use. (default: 'USD')
 */
export const isCurrencyInRange = function (value, min, max) {
  let currency = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 'USD';
  if (!value) return false;
  const numberValue = parseNumberFromCurrencyValue(value, currency);
  return isNumberInRange(numberValue, min, max);
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {string} value - The currency value to be validated.
 * @param {number} max - The maximum value allowed.
 * @param {string} currency - The currency code (ISO 4217) to use. (default: 'USD')
 */
export const isCurrencyLessThanOrEqualTo = function (value, max) {
  let currency = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'USD';
  if (!value) return false;
  const numberValue = parseNumberFromCurrencyValue(value, currency);
  return isNumberLessThanOrEqualTo(numberValue, max);
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {string} value - The currency value to be validated.
 * @param {number} min - The minimum value allowed.
 * @param {string} currency - The currency code (ISO 4217) to use. (default: 'USD')
 */
export const isCurrencyGreaterThanOrEqualTo = function (value, min) {
  let currency = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'USD';
  if (!value) return false;
  const numberValue = parseNumberFromCurrencyValue(value, currency);
  return isNumberGreaterThanOrEqualTo(numberValue, min);
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
export const isNumberInRange = (value, min, max) => {
  if (typeof value !== 'number') return false;
  return !isNaN(value) && value >= min && value <= max;
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {number} value
 * @param {number} max
 */
export const isNumberLessThanOrEqualTo = (value, max) => {
  if (typeof value !== 'number') return false;
  return !isNaN(value) && value <= max;
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 * @param {number} value
 * @param {number} min
 */
export const isNumberGreaterThanOrEqualTo = (value, min) => {
  if (typeof value !== 'number') return false;
  return !isNaN(value) && value >= min;
};

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const containsLetter = value => matchesPattern(value, /[a-zA-Z]/);

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const startsWithLetterOrNumber = value => matchesPattern(value, /^[a-zA-Z0-9]/);

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const containsUppercase = value => matchesPattern(value, /[A-Z]/);

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const containsNumber = value => matchesPattern(value, /\d/);

/**
 * @type {import('../hooks/useFormValidation.js').Validator}
 */
export const containsLettersNumbersOrSpecialCharacters = value => matchesPattern(value, /^[a-zA-Z0-9_.\-@]+$/);

/**
 * @param {string} value
 * @param {RegExp} pattern
 * @returns {boolean}
 */
export const matchesPattern = (value, pattern) => {
  return value !== null && pattern.test(value);
};

/**
 * Required file validation
 * @param {File} value
 * @returns {boolean}
 */
export const fileIsRequired = value => {
  return value && value instanceof File && value.size > 0 && value.name !== '';
};
fileIsRequired.errorMessage = 'File is required.';

/**
 * Is the file an image?
 * @param {File} value
 * @returns {boolean}
 */
export const fileIsImage = value => {
  return fileIsRequired(value) && value.type.startsWith('image/');
};
fileIsImage.errorMessage = 'File must be an image.';

/**
 * Is the file a PDF?
 * @param {File} value
 * @returns {boolean}
 */
export const fileIsPdf = value => {
  return fileIsRequired(value) && value.type === 'application/pdf';
};
fileIsPdf.errorMessage = 'File must be a PDF.';

/**
 * Is the file size less than or equal to the specified size?
 * @param {File} value
 * @param {number} max - max file size, in bytes
 * @returns {boolean}
 */
export const fileSizeIsLessThanOrEqualTo = (value, max) => {
  return fileIsRequired(value) && value.size <= max;
};

/**
 * Is the file name length less than or equal to the specified length?
 * @param {File} value
 * @param {number} max - max character length, including the file extension
 * @returns {boolean}
 */
export const fileNameIsLessThanOrEqualTo = (value, max) => {
  return fileIsRequired(value) && value.name.length <= max;
};

/**
 * Does the file name match the specified pattern?
 * @param {File} value
 * @param {RegExp} pattern
 * @returns {boolean}
 */
export const fileNameMatchesPattern = (value, pattern) => {
  return fileIsRequired(value) && pattern.test(value.name);
};

/**
 * Does the file name _not_ match the specified pattern?
 * @param {File} value
 * @param {RegExp} pattern
 * @returns {boolean}
 */
export const fileNameDoesNotMatchPattern = (value, pattern) => {
  return fileIsRequired(value) && pattern.test(value.name) === false;
};

/**
 * Are there less than or equal to N values?
 * Important! This is a GROUP validation method,
 * and must be used with the `formValidationCombinators.validateGroup` function.
 *
 * @param {Array} values
 * @param {number} n
 * @returns {boolean}
 */
export const lessThanOrEqualToNValues = (values, n) => {
  if (!Array.isArray(values)) {
    return true;
  }
  return values.length <= n;
};